import Filter from 'components/commons/filter'
import { useEffect, useState } from 'react'
import { useFetch } from 'services/useFetch'
import { API_LEAVE_ANALYTICS } from 'services/apiLeaveAnalytics'
import { deepClone, getHeadUnitText } from 'utils/utils'
import { getYears } from 'utils/utils'

const FILTER = {
    YEAR: 'year',
    CATEGORY: 'category',
    COMPANY: 'company',
    DEPARTMENT: 'department',
    DIVISION: 'division',
    SUB_DIVISION: 'sub-division',
}

const QUERY_PARAMS = {
    [FILTER.CATEGORY]: 'leave_ids',
    [FILTER.COMPANY]: 'company_ids',
    [FILTER.DEPARTMENT]: 'department_ids',
    [FILTER.DIVISION]: 'division_ids',
    [FILTER.SUB_DIVISION]: 'subdivision_ids',
}

const INITIAL_DATA_FILTER = [
    { label: 'Year', value: FILTER.YEAR, type: 'select', placeholder: 'Pilih tahun' },
    { label: 'Category', value: FILTER.CATEGORY, valueKey: 'leave_ids' },
    { label: 'Company', value: FILTER.COMPANY },
    { label: 'Departement', value: FILTER.DEPARTMENT },
    { label: 'Divisi', value: FILTER.DIVISION },
    { label: 'Sub Divisi', value: FILTER.SUB_DIVISION },
]

const JOB_UNIT = [FILTER.DEPARTMENT, FILTER.DIVISION, FILTER.SUB_DIVISION]

const INITIAL_OPTIONS = {
    [FILTER.YEAR]: getYears(2026),
    [FILTER.CATEGORY]: [],
    [FILTER.COMPANY]: [],
    [FILTER.DEPARTMENT]: [],
    [FILTER.DIVISION]: [],
    [FILTER.SUB_DIVISION]: [],
}

const INITIAL_FILTER = {
    year: null,
    leave_ids: [],
    company_ids: [],
    department_ids: [],
    division_ids: [],
    subdivision_ids: [],
}

export const LeaveAnalyticsFilterContainer = ({ queryFilter, onSubmit }) => {
    const [show, setShow] = useState(false)
    const [activeFilter, setActiveFilter] = useState(INITIAL_DATA_FILTER[0])
    const [options, setOptions] = useState(INITIAL_OPTIONS)
    const [filter, setFilter] = useState(INITIAL_FILTER)
    const [filterCount, setFilterCount] = useState()
    const { fetching: getJobUnit } = useFetch(API_LEAVE_ANALYTICS.LIST_JOB_UNIT)
    const { fetching: getCompany } = useFetch(API_LEAVE_ANALYTICS.COMPANY)
    const { fetching: getCategory } = useFetch(API_LEAVE_ANALYTICS.CATEGORY)

    const convertValue = (value) => {
        return String(value)
            .split(',')
            .map((v) => v.trim())
    }

    const groupCategoryValues = (selectedValues, categoryOptions) => {
        if (!selectedValues) return []

        let remaining = selectedValues
        let result = []

        categoryOptions?.forEach((item) => {
            const leaveIds = (item?.leave_ids || []).map((value) => String(value).trim())
            const isMatch = leaveIds.every((id) => remaining.includes(id))

            if (isMatch) {
                result.push(leaveIds.join(','))
                remaining = remaining.filter((id) => !leaveIds.includes(id))
            }
        })

        return result
    }

    useEffect(() => {
        const init = async () => {
            let categoryOptions = options[FILTER.CATEGORY]

            if (queryFilter?.[QUERY_PARAMS[FILTER.CATEGORY]]?.length && !categoryOptions?.length) {
                const response = await getCategory({ year: queryFilter?.year })
                categoryOptions = response?.data || []

                setOptions((prev) => ({
                    ...prev,
                    [FILTER.CATEGORY]: categoryOptions,
                }))
            }

            if (queryFilter) {
                const newQueryFilter = deepClone(queryFilter)

                if (newQueryFilter[FILTER.YEAR]) {
                    newQueryFilter[FILTER.YEAR] = Number(newQueryFilter[FILTER.YEAR])
                }

                if (newQueryFilter[QUERY_PARAMS[FILTER.CATEGORY]]?.length) {
                    newQueryFilter[QUERY_PARAMS[FILTER.CATEGORY]] = groupCategoryValues(
                        newQueryFilter[QUERY_PARAMS[FILTER.CATEGORY]],
                        categoryOptions,
                    )
                }

                setFilter(newQueryFilter)
                countFilterLength(newQueryFilter)
            }
        }

        init()
    }, [queryFilter])

    const onSelectFilter = (item) => {
        if (JOB_UNIT.includes(item?.value) && options[item?.value].length === 0) {
            getJobUnit(
                {
                    company_ids: filter?.company_ids,
                    head_unit_ids: filter?.[QUERY_PARAMS[getHeadUnitText[item?.value]]] || [],
                },
                item?.value,
            ).then((res) => setOptions({ ...options, [item?.value]: res?.data }))
        }

        if (item.value === FILTER.COMPANY && options[item?.value].length === 0) {
            getCompany().then((res) => setOptions({ ...options, [FILTER.COMPANY]: res?.data }))
        }

        if (item.value === FILTER.CATEGORY && options[item?.value].length === 0) {
            getCategory({ year: filter?.year }).then((res) => setOptions({ ...options, [FILTER.CATEGORY]: res?.data }))
        }

        setActiveFilter(item)
    }

    const onChangeOption = (item) => {
        const { name, value, checked } = item
        let arrChecked = filter?.[QUERY_PARAMS[name]] || []
        handleClearOptionsFilter(name)

        if (checked) {
            arrChecked.push(String(value))
        } else {
            arrChecked = arrChecked.filter((item) => item !== value)
        }

        setFilter({ ...filter, [QUERY_PARAMS[name]]: arrChecked })
    }

    const handleClearOptionsFilter = (filterName) => {
        if (filterName === FILTER.YEAR) {
            options[FILTER.CATEGORY] = []
            filter[[QUERY_PARAMS[FILTER.CATEGORY]]] = []
        } else if (filterName === FILTER.COMPANY) {
            options[FILTER.DEPARTMENT] = []
            options[FILTER.DIVISION] = []
            options[FILTER.SUB_DIVISION] = []
            filter[[QUERY_PARAMS[FILTER.DEPARTMENT]]] = []
            filter[[QUERY_PARAMS[FILTER.DIVISION]]] = []
            filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        } else if (filterName === FILTER.DEPARTMENT) {
            options[FILTER.DIVISION] = []
            options[FILTER.SUB_DIVISION] = []
            filter[[QUERY_PARAMS[FILTER.DIVISION]]] = []
            filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        } else if (filterName === FILTER.DIVISION) {
            options[FILTER.SUB_DIVISION] = []
            filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        }

        setOptions({ ...options })
    }

    const onChangeSelect = (e) => {
        const { name, value } = e

        handleClearOptionsFilter(name)
        setFilter({ ...filter, [name]: value?.value })
    }

    const onSubmitFilter = () => {
        const queryFilter = { ...filter }
        Object.entries(queryFilter).forEach(([key, item]) => {
            if (Array.isArray(queryFilter[key])) queryFilter[key] = item.join(',')
        })
        onSubmit(queryFilter)
        setShow(false)
    }

    const optionCheckedHandler = (name, option) => {
        const key = QUERY_PARAMS[name]
        const optionValue = option[activeFilter.valueKey || 'id']
        const optionArr = [].concat(optionValue).flatMap((value) => convertValue(value))
        const selected = [].concat(filter?.[key] || []).flatMap((value) => convertValue(value))

        return optionArr.every((value) => selected.includes(value))
    }

    const countFilterLength = (filter) => {
        let totalLength = 0
        for (const key in filter) {
            if (filter.hasOwnProperty(key)) {
                if (Array.isArray(filter[key])) {
                    totalLength += filter[key].length
                } else if (filter[key]) {
                    totalLength += 1
                }
            }
        }

        setFilterCount(totalLength)
    }

    const props = {
        show,
        onShow: () => setShow(true),
        onClose: () => setShow(false),
        queryParams: QUERY_PARAMS,
        filterList: INITIAL_DATA_FILTER,
        activeFilter,
        onSelectFilter,
        filter,
        filterCount,
        options,
        onChangeOption,
        onChangeSelect,
        onSubmitFilter,
        onClearFilter: () => setFilter(deepClone(INITIAL_FILTER)),
        optionCheckedHandler,
    }

    return <Filter {...props} />
}
